import React, { useEffect, useRef, useState } from 'react';
import { Contact, FileText, Plus, RefreshCw } from 'lucide-react';
import { PARTIES_THEME } from './partiesTheme';
import PartyFormModal from './PartyFormModal';
import PartyListPanel from './PartyListPanel';

export default function PartiesCatalogTab({
  formSectionRef,
  editingPartyId,
  buyerCount,
  supplierCount,
  totalCount,
  message,
  formProps,
  listProps,
  isMobileDevice,
  onSyncContacts,
  onSelectContact,
  syncingContacts,
}) {
  const theme = PARTIES_THEME;
  const [isFormOpen, setIsFormOpen] = useState(false);
  const openForm = () => setIsFormOpen(true);
  const closeForm = () => setIsFormOpen(false);
  const prevEditId = useRef(editingPartyId);

  useEffect(() => {
    if (editingPartyId) setIsFormOpen(true);
  }, [editingPartyId]);

  useEffect(() => {
    if (prevEditId.current && !editingPartyId) setIsFormOpen(false);
    prevEditId.current = editingPartyId;
  }, [editingPartyId]);

  const isError =
    message &&
    (message.includes('Error') || message.toLowerCase().includes('error'));

  return (
    <div className="flex flex-col flex-1 min-h-0 h-full w-full gap-3 overflow-visible lg:overflow-hidden">
      {message && (
        <div
          role="status"
          className={`shrink-0 rounded-xl px-3 py-2 text-sm border ${
            isError ? theme.messageErr : theme.messageOk
          }`}
        >
          {message}
        </div>
      )}

      <div className={`flex flex-col flex-1 min-h-0 h-full w-full ${theme.contentCard} overflow-visible lg:overflow-hidden`}>
        <div className={theme.panelHeader}>
          <div className="flex flex-wrap items-center gap-3 w-full">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <FileText className={`w-5 h-5 flex-shrink-0 ${theme.iconAccent}`} aria-hidden />
              <h2 className="text-base sm:text-lg font-bold text-slate-900 truncate">
                {theme.historyTitle}
              </h2>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className={theme.statPill}>
                <span className={theme.statPillStrong}>{totalCount}</span> total
              </span>
              <span className={theme.statPill}>
                <span className={theme.statPillStrong}>{buyerCount}</span> customers
              </span>
              <span className={theme.statPill}>
                <span className={theme.statPillStrong}>{supplierCount}</span> suppliers
              </span>
            </div>

            <div className="flex items-center gap-1.5 w-full sm:w-auto sm:ml-auto">
              {isMobileDevice && (
                <>
                  <button
                    type="button"
                    onClick={onSyncContacts}
                    disabled={syncingContacts}
                    title="Sync contacts"
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  >
                    <RefreshCw
                      className={`h-3.5 w-3.5 ${syncingContacts ? 'animate-spin' : ''}`}
                      aria-hidden
                    />
                    Sync
                  </button>
                  <button
                    type="button"
                    onClick={onSelectContact}
                    title="Phone book"
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    <Contact className="h-3.5 w-3.5" aria-hidden />
                    Phone
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={openForm}
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white rounded-xl bg-gradient-to-r ${theme.btnAddParty} transition-all hover:scale-[1.02] active:scale-[0.98]`}
              >
                <Plus className="h-4 w-4 shrink-0" aria-hidden />
                Add party
              </button>
            </div>
          </div>
        </div>

        <PartyListPanel {...listProps} embedded listOnly onAddClick={openForm} />
      </div>

      <PartyFormModal
        isOpen={isFormOpen}
        onClose={closeForm}
        formSectionRef={formSectionRef}
        editingPartyId={editingPartyId}
        formProps={{
          ...formProps,
          clearPartyForm: () => {
            formProps.clearPartyForm();
            closeForm();
          },
        }}
      />
    </div>
  );
}
